/**
 * Audit Dashboard Page
 * Sprint 14: Full Audit Module
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  ClipboardCheck,
  Plus,
  Search,
  Filter,
  Users,
  Building2,
  UserCheck,
  Calendar,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  Clock,
  Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

const AUDIT_TYPES = [
  { value: 'CLIENT_FILE', label: 'Client File Audit', icon: Users, description: 'Audit individual client records' },
  { value: 'EMPLOYEE_FILE', label: 'Employee File Audit', icon: UserCheck, description: 'Audit personnel/caregiver files' },
  { value: 'AGENCY_LEVEL', label: 'Agency Level Audit', icon: Building2, description: 'Audit agency-wide compliance' },
];

const STATUS_COLORS = {
  DRAFT: 'bg-slate-100 text-slate-700',
  IN_PROGRESS: 'bg-blue-100 text-blue-700',
  COMPLETE: 'bg-green-100 text-green-700',
  LOCKED: 'bg-purple-100 text-purple-700',
};

const AuditDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // New audit dialog
  const [showNewAuditDialog, setShowNewAuditDialog] = useState(false);
  const [newAuditType, setNewAuditType] = useState('');
  const [clients, setClients] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [auditNotes, setAuditNotes] = useState('');
  const [creating, setCreating] = useState(false);

  const canCreate = user?.role === 'Admin' || user?.role === 'ComplianceManager';

  const fetchAudits = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterType !== 'all') params.append('audit_type', filterType);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      
      const response = await api.get(`/audits?${params.toString()}`);
      setAudits(response.data.audits || []);
    } catch (error) {
      console.error('Error fetching audits:', error);
      toast.error('Failed to load audits');
    } finally {
      setLoading(false);
    }
  }, [filterType, filterStatus]);

  const fetchClients = async () => {
    try {
      const response = await api.get('/clients?page_size=100&status=Active');
      setClients(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchPersonnel = async () => {
    try {
      const response = await api.get('/personnel?page_size=100&is_active=true');
      setPersonnel(response.data.personnel || []);
    } catch (error) {
      console.error('Error fetching personnel:', error);
    }
  };

  useEffect(() => {
    fetchAudits();
  }, [fetchAudits]);

  const handleOpenNewAudit = (type) => {
    setNewAuditType(type);
    setSelectedSubject('');
    setAuditNotes('');
    
    if (type === 'CLIENT_FILE') {
      fetchClients();
    } else if (type === 'EMPLOYEE_FILE') {
      fetchPersonnel();
    }
    
    setShowNewAuditDialog(true);
  };

  const handleCreateAudit = async () => {
    try {
      setCreating(true);
      
      const payload = {
        audit_type: newAuditType,
        notes: auditNotes,
      };
      
      if (newAuditType === 'CLIENT_FILE') {
        if (!selectedSubject) {
          toast.error('Please select a client');
          return;
        }
        payload.subject_client_id = selectedSubject;
      } else if (newAuditType === 'EMPLOYEE_FILE') {
        if (!selectedSubject) {
          toast.error('Please select an employee');
          return;
        }
        payload.subject_personnel_id = selectedSubject;
      }
      
      const response = await api.post('/audits', payload);
      toast.success('Audit created successfully');
      setShowNewAuditDialog(false);
      navigate(`/audits/${response.data.id}`);
    } catch (error) {
      console.error('Error creating audit:', error);
      toast.error(error.response?.data?.detail || 'Failed to create audit');
    } finally {
      setCreating(false);
    }
  };

  const filteredAudits = audits.filter(audit => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      audit.id.toLowerCase().includes(search) ||
      audit.subject_client_name?.toLowerCase().includes(search) ||
      audit.subject_personnel_name?.toLowerCase().includes(search) ||
      audit.audit_type.toLowerCase().includes(search)
    );
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'DRAFT': return <Clock className="h-4 w-4" />;
      case 'IN_PROGRESS': return <AlertTriangle className="h-4 w-4" />;
      case 'COMPLETE': return <CheckCircle className="h-4 w-4" />;
      case 'LOCKED': return <Lock className="h-4 w-4" />;
      default: return null;
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="audit-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <ClipboardCheck className="h-6 w-6 text-avida-blue" />
              Audit Module
            </h1>
            <p className="text-slate-600 mt-1">
              DHS/ForwardHealth compliance audits
            </p>
          </div>
        </div>

        {/* Quick Actions - Create New Audit */}
        {canCreate && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {AUDIT_TYPES.map(type => {
              const Icon = type.icon;
              return (
                <Card 
                  key={type.value}
                  className="cursor-pointer hover:border-avida-blue hover:shadow-md transition-all"
                  onClick={() => handleOpenNewAudit(type.value)}
                  data-testid={`create-${type.value.toLowerCase()}-btn`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-lg bg-avida-blue/10">
                        <Icon className="h-6 w-6 text-avida-blue" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900">{type.label}</h3>
                        <p className="text-sm text-slate-500 mt-1">{type.description}</p>
                      </div>
                      <Plus className="h-5 w-5 text-slate-400" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search audits..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="audit-search"
                />
              </div>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-full md:w-48" data-testid="filter-type">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {AUDIT_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full md:w-48" data-testid="filter-status">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="DRAFT">Draft</SelectItem>
                  <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                  <SelectItem value="COMPLETE">Complete</SelectItem>
                  <SelectItem value="LOCKED">Locked</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Audits List */}
        <Card>
          <CardHeader>
            <CardTitle>Audits ({filteredAudits.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-slate-500">Loading audits...</div>
            ) : filteredAudits.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No audits found. {canCreate && "Click a card above to create your first audit."}
              </div>
            ) : (
              <div className="space-y-3">
                {filteredAudits.map(audit => (
                  <div
                    key={audit.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/audits/${audit.id}`)}
                    data-testid={`audit-row-${audit.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="p-2 rounded-lg bg-slate-100">
                        {audit.audit_type === 'CLIENT_FILE' && <Users className="h-5 w-5 text-slate-600" />}
                        {audit.audit_type === 'EMPLOYEE_FILE' && <UserCheck className="h-5 w-5 text-slate-600" />}
                        {audit.audit_type === 'AGENCY_LEVEL' && <Building2 className="h-5 w-5 text-slate-600" />}
                      </div>
                      <div>
                        <div className="font-medium text-slate-900">
                          {audit.audit_type.replace('_', ' ')}
                          {audit.subject_client_name && ` - ${audit.subject_client_name}`}
                          {audit.subject_personnel_name && ` - ${audit.subject_personnel_name}`}
                        </div>
                        <div className="text-sm text-slate-500 flex items-center gap-2">
                          <Calendar className="h-3 w-3" />
                          {new Date(audit.created_at).toLocaleDateString()}
                          {audit.created_by_name && ` • ${audit.created_by_name}`}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {audit.summary && (
                        <div className="hidden md:flex items-center gap-2 text-sm">
                          <span className="text-green-600">{audit.summary.compliant} ✓</span>
                          <span className="text-yellow-600">{audit.summary.partial} ◐</span>
                          <span className="text-red-600">{audit.summary.non_compliant} ✗</span>
                          {audit.summary.open_capas > 0 && (
                            <Badge variant="destructive" className="ml-2">
                              {audit.summary.open_capas} CAPA
                            </Badge>
                          )}
                        </div>
                      )}
                      <Badge className={STATUS_COLORS[audit.status]}>
                        {getStatusIcon(audit.status)}
                        <span className="ml-1">{audit.status}</span>
                      </Badge>
                      <ChevronRight className="h-5 w-5 text-slate-400" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* New Audit Dialog */}
        <Dialog open={showNewAuditDialog} onOpenChange={setShowNewAuditDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                Create {AUDIT_TYPES.find(t => t.value === newAuditType)?.label}
              </DialogTitle>
              <DialogDescription>
                Start a new audit following the DHS/ForwardHealth checklist.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {newAuditType === 'CLIENT_FILE' && (
                <div className="space-y-2">
                  <Label>Select Client *</Label>
                  <Select value={selectedSubject} onValueChange={setSelectedSubject}>
                    <SelectTrigger data-testid="select-client">
                      <SelectValue placeholder="Choose a client..." />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map(client => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.first_name} {client.last_name} ({client.ma_number || 'N/A'})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {newAuditType === 'EMPLOYEE_FILE' && (
                <div className="space-y-2">
                  <Label>Select Employee *</Label>
                  <Select value={selectedSubject} onValueChange={setSelectedSubject}>
                    <SelectTrigger data-testid="select-employee">
                      <SelectValue placeholder="Choose an employee..." />
                    </SelectTrigger>
                    <SelectContent>
                      {personnel.map(person => (
                        <SelectItem key={person.id} value={person.id}>
                          {person.first_name} {person.last_name} ({person.role})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {newAuditType === 'AGENCY_LEVEL' && (
                <div className="p-4 bg-blue-50 rounded-lg text-sm text-blue-700">
                  Agency level audits cover organization-wide compliance requirements (C1-C8).
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Textarea
                  placeholder="Add any notes about this audit..."
                  value={auditNotes}
                  onChange={(e) => setAuditNotes(e.target.value)}
                  data-testid="audit-notes"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewAuditDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateAudit} 
                disabled={creating}
                data-testid="confirm-create-audit"
              >
                {creating ? 'Creating...' : 'Create Audit'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AuditDashboard;
